import React from 'react';
import { Check, Clock, RotateCcw, X, MinusCircle } from 'lucide-react';
import type { RequisitionApprovalStep } from '../api';
import { APPROVAL_STEP_LABELS, formatDateTimeBR } from './format';

const ICONS: Record<string, React.ReactNode> = {
  aprovado: <Check className="w-4 h-4 text-green-600" />,
  reprovado: <X className="w-4 h-4 text-red-600" />,
  devolvido: <RotateCcw className="w-4 h-4 text-amber-600" />,
  pendente: <Clock className="w-4 h-4 text-blue-600" />,
  encerrado: <MinusCircle className="w-4 h-4 text-gray-400" />,
};

interface TrilhaAprovacaoProps {
  approvals: RequisitionApprovalStep[];
  /** Ciclo corrente da requisição — os anteriores viram histórico. */
  currentCycle: number;
}

/**
 * Cadeia de aprovação da requisição.
 *
 * Mostra o ciclo corrente em destaque e os ciclos anteriores recolhidos:
 * depois de uma devolução, a cadeia reinicia, e misturar os ciclos numa
 * lista só faria parecer que o mesmo passo foi decidido duas vezes.
 */
export function TrilhaAprovacao({ approvals, currentCycle }: TrilhaAprovacaoProps) {
  const cycles = [...new Set(approvals.map((step) => step.cycle))].sort((a, b) => b - a);

  if (approvals.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        Nenhum passo de aprovação — a requisição ainda está em rascunho.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {cycles.map((cycle) => {
        const steps = approvals
          .filter((step) => step.cycle === cycle)
          .sort((a, b) => a.sortOrder - b.sortOrder);
        const isCurrent = cycle === currentCycle;

        return (
          <div key={cycle} className={isCurrent ? '' : 'opacity-60'}>
            {cycles.length > 1 && (
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                {isCurrent ? 'Ciclo atual' : `Ciclo anterior (${cycle + 1}ª tentativa)`}
              </p>
            )}
            <ol className="space-y-3">
              {steps.map((step) => (
                <li key={step.id} className="flex gap-3">
                  <div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-gray-100">
                    {ICONS[step.decision] ?? ICONS.pendente}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="text-sm font-medium text-gray-900">
                        {APPROVAL_STEP_LABELS[step.stepKey] ?? step.stepKey}
                      </span>
                      <span className="text-xs text-gray-500">
                        {step.decision === 'pendente'
                          ? 'aguardando decisão'
                          : formatDateTimeBR(step.decidedAt)}
                      </span>
                    </div>
                    {step.parecer && (
                      <p className="mt-1 whitespace-pre-wrap text-sm text-gray-600">
                        {step.parecer}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        );
      })}
    </div>
  );
}
